import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, KeyRound } from "lucide-react";
import { NAV, ALCOTT } from "@/data/alcott";
import { IMAGES } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

export const Masthead = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isActive = (to) => (to === "/" ? pathname === "/" : to ? pathname.startsWith(to) : false);

  const NavItem = ({ item }) => {
    const cls = `whitespace-nowrap font-body text-[0.68rem] uppercase tracking-[0.16em] transition-colors duration-300 hover:text-[#C05C42] ${
      isActive(item.to) ? "text-[#C05C42]" : "text-[#0A0A0A]/70"
    }`;
    const testid = `nav-${item.label.replace(/\s+/g, "-").toLowerCase()}`;
    if (item.external)
      return (
        <a href={item.href} target="_blank" rel="noreferrer" className={cls} data-testid={testid}>
          {item.label}
        </a>
      );
    return (
      <Link to={item.to} className={cls} data-testid={testid}>
        {item.label}
      </Link>
    );
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease }}
      className="sticky top-0 z-50 w-full border-b border-[#0A0A0A]/10 bg-[#FAF9F6]/80 backdrop-blur-xl"
      data-testid="main-navigation"
    >
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="flex h-24 items-center justify-between md:h-28">
          {/* left nav */}
          <div className="hidden flex-1 items-center gap-4 nav:flex">
            {NAV.slice(0, 2).map((l) => (
              <NavItem key={l.label} item={l} />
            ))}
          </div>

          {/* center: brand lockup */}
          <Link
            to="/"
            data-testid="masthead-wordmark"
            className="flex flex-1 flex-col items-center justify-center gap-1"
          >
            <div className="flex items-center gap-3 md:gap-4">
              <img
                src={IMAGES.logo}
                alt="Sybils & Society."
                className="h-14 w-auto object-contain md:h-20"
                data-testid="masthead-logo"
              />
              <span className="whitespace-nowrap font-serif text-[1.3rem] font-semibold leading-none tracking-[0.02em] text-[#0A0A0A] md:text-[1.6rem]">
                Sybils &amp; Society.
              </span>
            </div>
            <span className="mt-0.5 hidden font-body text-[0.55rem] uppercase tracking-[0.4em] text-[#C05C42] md:block">
              Est. Culture · Memory · Mobility
            </span>
          </Link>

          {/* right: nav + entrance */}
          <div className="hidden flex-1 items-center justify-end gap-4 nav:flex">
            {NAV.slice(2).map((l) => (
              <NavItem key={l.label} item={l} />
            ))}
            <Link
              to="/veil"
              onClick={() => sessionStorage.removeItem("sybil_entered")}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-[#0A0A0A]/30 px-4 py-2.5 font-body text-[0.7rem] uppercase tracking-[0.2em] text-[#0A0A0A] transition-colors duration-300 hover:border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-[#FAF9F6]"
              data-testid="masthead-sybils-society-btn"
            >
              <KeyRound size={16} strokeWidth={1.75} /> Sybils Society
            </Link>
          </div>

          {/* mobile / tablet */}
          <button
            onClick={() => setOpen((o) => !o)}
            className="text-[#0A0A0A] nav:hidden"
            data-testid="masthead-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="overflow-hidden border-t border-[#0A0A0A]/10 bg-[#FAF9F6] nav:hidden"
          data-testid="masthead-mobile-menu"
        >
          <div className="flex flex-col gap-1 px-6 py-5">
            {NAV.map((l) =>
              l.external ? (
                <a
                  key={l.label}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setOpen(false)}
                  className="py-2 font-serif text-2xl font-light text-[#0A0A0A]"
                  data-testid={`mobile-nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {l.label}
                </a>
              ) : (
                <Link
                  key={l.label}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-2 font-serif text-2xl font-light text-[#0A0A0A]"
                  data-testid={`mobile-nav-${l.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {l.label}
                </Link>
              )
            )}
            <Link
              to="/veil"
              onClick={() => { setOpen(false); sessionStorage.removeItem("sybil_entered"); }}
              className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#0A0A0A]/25 px-4 py-2.5 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]"
            >
              <KeyRound size={13} /> Sybils Society
            </Link>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Masthead;
